import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/format";
import { History, Pencil, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RupiahInput } from "@/components/RupiahInput";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export function useIsAdmin() {
  const { user } = useAuth();
  return user?.role === "it_admin";
}

export function EditHistory({ entityType, entityId, refreshKey, filterPrefix, bare }) {
  const isAdmin = useIsAdmin();
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    if (!isAdmin || !entityId) return;
    api.get("/admin/edit-logs", { params: { entity_type: entityType, entity_id: entityId } })
      .then((r) => setLogs(r.data)).catch(() => {});
  }, [isAdmin, entityType, entityId, refreshKey]);
  if (!isAdmin) return null;
  const rows = filterPrefix ? logs.filter((l) => l.field.startsWith(filterPrefix)) : logs;
  const body = (
    <div data-testid={`edit-history-${entityType}`}>
      <h3 className="font-semibold text-sm flex items-center gap-2 mb-3" style={{ fontFamily: "Outfit" }}>
        <History className="h-4 w-4 text-brand" /> Riwayat Perubahan (IT Admin)
      </h3>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="edit-history-empty">Belum ada perubahan oleh IT Admin.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-xs" data-testid="edit-history-table">
            <thead className="bg-secondary text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Waktu</th><th className="text-left px-3 py-2">Field</th>
                <th className="text-left px-3 py-2">Sebelum</th><th className="text-left px-3 py-2">Sesudah</th>
                <th className="text-left px-3 py-2">Oleh</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id} className="border-t align-top" data-testid={`edit-log-${l.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(l.edited_at)}</td>
                  <td className="px-3 py-2 font-medium">{l.field}</td>
                  <td className="px-3 py-2 text-rose-600 break-words max-w-[220px]">{l.before || "-"}</td>
                  <td className="px-3 py-2 text-emerald-700 break-words max-w-[220px]">{l.after || "-"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{l.edited_by_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
  return bare ? body : <Card className="p-5">{body}</Card>;
}

export function EditExpenseDialog({ item, url, vehicles, onDone, testId, history }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [rk, setRk] = useState(0);
  useEffect(() => {
    if (open) setForm({ amount: item.amount ?? "", description: item.description || "",
      vehicle_id: item.vehicle_id || "", date: (item.uploaded_at || "").slice(0, 10) });
  }, [open, item]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(url, { ...form, amount: Number(form.amount) });
      toast.success("Pengeluaran diperbarui.");
      setRk((x) => x + 1); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-brand/40 text-brand" data-testid={testId}>
          <Pencil className="h-3 w-3 mr-1" /> Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-expense-dialog">
        <DialogHeader>
          <DialogTitle>Edit Pengeluaran</DialogTitle>
          <DialogDescription>Perubahan oleh IT Admin dicatat otomatis di riwayat perubahan.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Nominal</Label><RupiahInput min="0" value={form.amount ?? ""} onChange={set("amount")} className="mt-1.5 font-mono" data-testid="edit-expense-amount" /></div>
            <div><Label>Tanggal</Label><Input type="date" value={form.date || ""} onChange={set("date")} className="mt-1.5" data-testid="edit-expense-date" /></div>
          </div>
          <div><Label>Keterangan</Label><Input value={form.description || ""} onChange={set("description")} className="mt-1.5" data-testid="edit-expense-desc" /></div>
          <div>
            <Label>Kendaraan</Label>
            <select value={form.vehicle_id || ""} onChange={set("vehicle_id")} className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="edit-expense-vehicle">
              <option value="">Tanpa kendaraan</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{`${v.plate_number} — ${v.vehicle_type}`}</option>)}
            </select>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="save-edit-expense">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Perubahan"}
            </Button>
          </DialogFooter>
        </form>
        {history && <div className="pt-3 border-t"><EditHistory {...history} refreshKey={rk} bare /></div>}
      </DialogContent>
    </Dialog>
  );
}
